import { useDrawerStore } from '../../../store';
import React, { useEffect, useState } from 'react';
import {
  useCreateData,
  useFetchData,
  useUnitOfMeasureStore,
  useUpdateData,
} from '../../../hooks';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import {
  Resource,
  UnitConversionOperator,
  UomUnitType,
} from '../../../lib/enums';
import { Spin } from 'antd';
import FormDrawer from '../../../components/ui/FormDrawer';
import ErrorModal from '../../../components/ui/modal/ErrorModal';

const UnitOfMeasureForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  const drawerStore = useDrawerStore();
  const unitOfMeasureStore = useUnitOfMeasureStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const { data: baseUomData, isLoading: isbaseUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >(api, {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const onFinish = (values: { baseUom: string }) => {
    setSpinning(true);
    const baseUom: BaseModel | undefined = baseUomData?.data?.find((obj) => {
      return obj.id == values.baseUom;
    });

    const jsonData = {
      ...values,
      baseUom: baseUom,
    };
    if (unitOfMeasureStore.data !== null) {
      const updatedData = {
        ...unitOfMeasureStore.data,
        ...jsonData,
      };
      mutateUpdate({ id: unitOfMeasureStore.data.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  const conversionTypeOptions: BaseModel[] = Object.keys(
    UnitConversionOperator,
  ).map(
    (key) =>
      ({
        id: key,
        code: UnitConversionOperator[
          key as keyof typeof UnitConversionOperator
        ],
        description:
          UnitConversionOperator[key as keyof typeof UnitConversionOperator],
      }) as BaseModel,
  );

  const unitTypeOptions: BaseModel[] = Object.keys(UomUnitType).map(
    (key) =>
      ({
        id: key,
        code: UomUnitType[key as keyof typeof UomUnitType],
        description: UomUnitType[key as keyof typeof UomUnitType],
      }) as BaseModel,
  );

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: unitOfMeasureStore.data != null,
      defaultValue: unitOfMeasureStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: unitOfMeasureStore.data?.description,
    },
    {
      name: 'unitType',
      label: 'Unit Type',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: unitTypeOptions,
      defaultValue: unitOfMeasureStore.data?.unitType,
    },
    {
      name: 'baseUom',
      label: 'Base unit of measurement',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isbaseUomLoading,
      values: baseUomData?.data,
      defaultValue: unitOfMeasureStore.data?.baseUom?.id,
    },
    {
      name: 'conversionType',
      label: 'Conversion Type',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: conversionTypeOptions,
      defaultValue: unitOfMeasureStore.data?.conversionType,
    },
    {
      name: 'count',
      label: 'Count',
      defaultValue: unitOfMeasureStore.data?.count,
      type: 'numeric',
    },
    {
      name: 'factor',
      label: 'Factor',
      defaultValue: unitOfMeasureStore.data?.factor,
      type: 'numeric',
    },
    {
      name: 'rate',
      label: 'Rate',
      defaultValue: unitOfMeasureStore.data?.rate,
      type: 'numeric',
    },
  ];

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={unitOfMeasureStore.data}
        moduleName={moduleName}
        setData={unitOfMeasureStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default UnitOfMeasureForm;
